import { Divider, Group, LoadingOverlay, Stack } from '@mantine/core';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UseGroupsSwitch } from '../../../../../components/use-groups-switch/use-groups-switch';
import { resolvePhaseLabels } from '../../../../../helpers/phase-labels/phase-labels';
import { useGlobalStore } from '../../../../../store/global-store/global-store';
import { useFlowByPhaseTabStore } from '../../../../../store/strategy-store/strategy-store';
import {
  setFlowByPhaseMetric,
  setFlowByPhaseSelectedDate,
  setFlowByPhaseUseGroups,
} from '../../../../../store/strategy-store/strategy-store.actions';
import { secondaryBase, skyDark } from '../../../../../styles/design-tokens';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Select } from '../../../../../ui-library/select/select';
import { Text } from '../../../../../ui-library/typography/typography';
import { FloraButton } from '../../../../flora/flora-button';
import { MainSectionControls } from '../../components/main-section-controls/main-section-controls';
import { hasArrayData } from '../../initiative-performance.helpers';
import { getPhasesMetadata } from '../flow-by-phase-tab.constants';
import { formatFlowByPhaseDataForFlora } from '../flow-by-phase-tab.helpers';
import { useChartData, useDateOptions, useFlowByPhaseData } from '../flow-by-phase-tab.hooks';
import { FlowByPhaseChart } from './flow-by-phase-chart/flow-by-phase-chart';

export const MainSection = () => {
  const i18n = useTranslation();
  const useAI = useGlobalStore((state) => state.useAI);
  const metric = useFlowByPhaseTabStore((state) => state.metric);
  const selectedDate = useFlowByPhaseTabStore((state) => state.selectedDate);
  const selectedEpic = useFlowByPhaseTabStore((state) => state.selectedEpic || undefined);
  const useGroups = useFlowByPhaseTabStore((state) => state.useGroups);
  const phases = useFlowByPhaseTabStore((state) => state.phases);
  const chartRef = useRef<HTMLDivElement>(null!);
  const { chartData, currentDate } = useChartData();
  const dateOptions = useDateOptions();

  const { isLoading } = useFlowByPhaseData(selectedEpic);

  const { t } = useTranslation();
  const resolvedLabels = useMemo(() => resolvePhaseLabels(phases ?? undefined, t), [phases, t]);

  const { floraPrompt, floraData } = useMemo(() => {
    if (hasArrayData(chartData) && currentDate) {
      return {
        floraPrompt: i18n.t('flora.strategy.flow_by_phase_tab.chart_prompt'),
        floraData: formatFlowByPhaseDataForFlora(chartData, metric, getPhasesMetadata(t, resolvedLabels)),
      };
    }
    return { floraPrompt: '', floraData: {} };
  }, [chartData, metric, currentDate, i18n.t, t, resolvedLabels]);

  const title = (
    <Group>
      <Text size="large" weight="bold">
        {t('strategy.initiative_performance.flow_by_phase')}
      </Text>
      {hasArrayData(dateOptions) && (
        <>
          <Select
            size="sm"
            options={dateOptions}
            value={selectedDate || ''}
            onChange={setFlowByPhaseSelectedDate}
            placeholder={t('common.placeholders.select_date')}
            style={{ width: 160 }}
            rightSection={<Icon name="calendar_month" size={16} color={skyDark} />}
            rightSectionPointerEvents="none"
          />
          <Divider orientation="vertical" />
          <UseGroupsSwitch checked={useGroups} onChange={(checked) => setFlowByPhaseUseGroups(checked)} />
        </>
      )}
    </Group>
  );

  return (
    <Stack h="100%" style={{ overflow: 'hidden', position: 'relative' }}>
      <LoadingOverlay visible={isLoading} loaderProps={{ color: secondaryBase }} />
      <MainSectionControls
        title={title}
        chartRef={chartRef}
        metric={metric}
        setMetric={setFlowByPhaseMetric}
        disableControls={!currentDate || !hasArrayData(chartData)}
        floraButton={
          useAI ? (
            <FloraButton
              externalData={[floraData]}
              prompt={floraPrompt}
              tooltipText={i18n.t('flora.strategy.flora_button_tooltip')}
            />
          ) : undefined
        }
      />
      <FlowByPhaseChart ref={chartRef} />
    </Stack>
  );
};
